import React, { useEffect, useRef } from "react";
import * as echarts from "echarts";
import type { ScoreDimension } from "src/data/equityResearchTypes";

interface BusinessScoreRadarProps {
  dimensions: ScoreDimension[];
  darkMode?: boolean;
}

const BusinessScoreRadar: React.FC<BusinessScoreRadarProps> = ({
  dimensions,
  darkMode,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.EChartsType | null>(null);

  useEffect(() => {
    if (!chartRef.current) return;

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    const indicator = dimensions.map((d) => ({
      name: d.shortLabel,
      max: 10,
    }));

    const values = dimensions.map((d) => d.score);

    chartInstance.current.setOption({
      backgroundColor: "transparent",
      tooltip: {
        trigger: "item",
        formatter: (params: { value: number[] }) => {
          return dimensions
            .map(
              (d, i) => `${d.label}: <b>${params.value[i]?.toFixed(1)}/10</b>`,
            )
            .join("<br/>");
        },
      },
      radar: {
        indicator,
        radius: "68%",
        center: ["50%", "52%"],
        splitNumber: 4,
        axisName: {
          color: darkMode ? "#cbd5e1" : "#475569",
          fontSize: 11,
        },
        splitLine: {
          lineStyle: { color: darkMode ? "#334155" : "#e2e8f0" },
        },
        splitArea: {
          areaStyle: {
            color: darkMode
              ? ["rgba(48,50,78,0.25)", "rgba(57,69,100,0.35)"]
              : ["rgba(244,245,248,0.8)", "rgba(208,212,228,0.5)"],
          },
        },
        axisLine: {
          lineStyle: { color: darkMode ? "#394564" : "#d0d4e4" },
        },
      },
      series: [
        {
          type: "radar",
          data: [
            {
              value: values,
              name: "Business Score",
              areaStyle: {
                color: new echarts.graphic.RadialGradient(0.5, 0.5, 1, [
                  { offset: 0, color: "rgba(0, 168, 95, 0.4)" },
                  { offset: 1, color: "rgba(59, 130, 246, 0.12)" },
                ]),
              },
              lineStyle: { color: "#00A85F", width: 2 },
              itemStyle: { color: "#3b82f6" },
              symbol: "circle",
              symbolSize: 6,
            },
          ],
          animationDuration: 1200,
          animationEasing: "cubicOut",
        },
      ],
    });

    const handleResize = () => chartInstance.current?.resize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, [dimensions, darkMode]);

  return <div ref={chartRef} className="w-full h-[220px] sm:h-[240px]" />;
};

export default React.memo(BusinessScoreRadar);
